// ─── Display-only FX — reference conversion for product catalog pages ───
// Per GBCOSPACK decision, product list/detail secondary currency uses
// fx_rates_current directly as a "customer-safe display FX" reference.
// This is DISPLAY/REFERENCE only — NEVER used for official quote totals.

import { getSupabaseClient } from '@/lib/supabase';

const cache = new Map<string, { rate: number; fetchedAt: number }>();
const TTL_MS = 30 * 60 * 1000; // 30 minutes

/** Fetch the display FX rate for 1 USD → quoteCurrency. Cached; USD returns 1. */
export async function fetchDisplayFxRate(quoteCurrency: string): Promise<number | null> {
  const code = (quoteCurrency || '').toUpperCase();
  if (!code || code === 'USD') return 1;

  const cached = cache.get(code);
  if (cached && Date.now() - cached.fetchedAt < TTL_MS) return cached.rate;

  const supabase = getSupabaseClient();
  if (!supabase) return null;

  const { data, error } = await supabase
    .from('fx_rates_current')
    .select('quote_currency_code, market_rate')
    .eq('base_currency_code', 'USD')
    .eq('quote_currency_code', code)
    .maybeSingle();

  if (error || !data) return null;

  const rate = Number((data as { market_rate?: unknown }).market_rate);
  if (!Number.isFinite(rate) || rate <= 0) return null;

  cache.set(code, { rate, fetchedAt: Date.now() });
  return rate;
}

/** Convert a USD reference amount to the selected currency for display only. */
export function convertUsdForDisplay(
  usdValue: string | number | null | undefined,
  rate: number | null,
): number | null {
  if (usdValue == null || usdValue === '') return null;
  const usd = Number(usdValue);
  if (!Number.isFinite(usd)) return null;
  if (rate == null || !Number.isFinite(rate)) return null;
  return usd * rate;
}